import { LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import dayjs from 'dayjs'
import Animated from '~/components/elements/Animated'
import Content from '~/components/elements/Content'
import type { Page_Flexcontent_Flex_Hubspot, RootQueryToExpertConnection } from '~/graphql/types'
import { type ProfilePageLoaderData } from '~/services/hubspot.server'
import { phases } from './HubspotBanner'

type Props = Page_Flexcontent_Flex_Hubspot & {
  subdata?: {
    Advisors: RootQueryToExpertConnection
  }
}

export default function HubspotHQ({ fields }: { fields: Props }) {
  const { hubspotData: profile } = useLoaderData<ProfilePageLoaderData>()
  const currentPhase = phases.find((phase) => phase.id.some((id) => String(id) === profile.deals[0].properties.dealstage))

  let advisorLastname = profile.owner.lastName
  const advisor = fields.subdata?.Advisors?.nodes.find((advisor) => {
    if (currentPhase.step === 2) {
      advisorLastname = profile.properties.carerix_first_meeting_owner_lastname
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_first_meeting_owner_firstname} ${profile.properties.carerix_first_meeting_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_first_meeting_owner_email?.toLowerCase()
      )
    }

    if (currentPhase.step === 4) {
      advisorLastname = profile.properties.carerix_job_owner_lastname
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_job_owner_firstname} ${profile.properties.carerix_job_owner_lastname}`.toLowerCase() ||
        advisor?.recap?.email?.toLowerCase() === profile.properties.carerix_job_owner_email?.toLowerCase()
      )
    }

    return (
      advisor.title?.toLowerCase() === `${profile.owner.firstName} ${profile.owner.lastName}`.toLowerCase() ||
      advisor?.recap?.email?.toLowerCase() === profile.owner.email?.toLowerCase()
    )
  })

  return (
    <section data-component="HubspotHQ" className="bg-site-secondair section--large--pt section--large--pb">
      <div className="container">
        <div className="max-w-[1119px] mx-auto">
          <div>
            <Animated delay={100}>
              <h2 className="border-text mb-4 md:-mb-2 lg:-mb-4 title--semi-unit text-transparent md:text-center">{fields.title}</h2>
            </Animated>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-16 lg:gap-x-24">
            <div className="max-md:order-last">
              <Animated delay={150}>
                <div className="relative h-[400px] w-full">
                  <Animated delay={300}>
                    <div className="h-[76px] w-[114px] bg-site-accent absolute top-[38px] lg:-top-[38px] -left-[57px]" />
                  </Animated>

                  <LossePlaatjie className="h-full w-full object-cover" src={fields.image} />
                </div>
              </Animated>
            </div>
            <Animated delay={300}>
              <div className="flex items-center">
                <Content className="children-p:text-lg children-strong:text-site-base">
                  {fields.description
                    ?.replaceAll('{firstname}', advisor?.recap?.firstname)
                    ?.replaceAll('{lastname}', advisorLastname)
                    ?.replace('{meeting_time}', dayjs(profile.properties.carerix_first_meeting_date).format('dddd DD MMMM [om] HH:mm'))
                    ?.replace('{meeting_name}', profile.properties.carerix_first_meeting_location_name)
                    ?.replace('{meeting_location}', profile.properties.carerix_first_meeting_location)}
                </Content>
              </div>
            </Animated>
          </div>
        </div>
      </div>
    </section>
  )
}
